import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { qaAudit } from '@platform/client';
import { qaAuditSchema } from '@platform/schema/qa-score';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { SEVERITY_COLOUR } from '../authoring/Findings';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import type { QaAudit as Audit } from '@platform/schema/qa-score';

/**
 * Published lessons with scenes a first-time learner cannot act on — per-scene QA.
 *
 * Read-only. Nothing here unpublishes or edits: each lesson links to its review workspace,
 * where the findings are accepted, fixed or dismissed like any other.
 */
export function QaAudit() {
  const audit = useQuery({
    queryKey: ['qa-audit'],
    queryFn: async () =>
      qaAuditSchema.parse((await qaAudit({ throwOnError: true })).data),
  });

  const lessons = audit.data?.lessons ?? [];

  return (
    <Stack spacing={SCREEN_GAP}>
      <PageHeader
        title="QA audit"
        subtitle="Published lessons with scenes a first-time learner may not follow. Nothing here is unpublished."
      />

      {audit.isPending && <LinearProgress />}
      {audit.isError && (
        <Alert severity="error">The audit could not be loaded.</Alert>
      )}
      {audit.isSuccess && lessons.length === 0 && (
        <Alert severity="success">No published scene is flagged.</Alert>
      )}

      {lessons.map((lesson) => (
        <LessonCard key={lesson.id} lesson={lesson} />
      ))}
    </Stack>
  );
}

function LessonCard({ lesson }: { lesson: Audit['lessons'][number] }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Link
          component={RouterLink}
          to={`/authoring/lessons/${lesson.id}`}
          variant="h6"
        >
          {lesson.title}
        </Link>

        {lesson.scenes.map((scene) => (
          <Stack key={scene.sceneId} spacing={1} component="section">
            <Typography variant="subtitle2">
              Scene {scene.position}: {scene.prompt}
            </Typography>

            {scene.findings.map((one) => (
              <Stack key={one.id} direction="row" spacing={1}>
                <Chip
                  size="small"
                  label={one.severity === 'BLOCKER' ? 'Blocker' : 'Warning'}
                  color={SEVERITY_COLOUR[one.severity]}
                />
                <Stack>
                  <Typography variant="body2">{one.finding}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {one.suggestion}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}
